<script lang="ts">
  import { onMount, createEventDispatcher } from "svelte";

  const dispatch = createEventDispatcher<{
    update: { value: string };
  }>();

  export let value = "";

  let ref: HTMLDivElement;
  let resizeObserver: ResizeObserver;

  onMount(() => {
    resizeObserver = new ResizeObserver(() => {
      if (!ref) return;
      dispatch("update", { value: ref?.innerText });
    });

    resizeObserver.observe(ref);

    // cleanup after component removed from DOM
    return () => {
      resizeObserver.disconnect();
    };
  });

  /*
  For content editable elements, the default behaviour on Enter is to insert a <div>/<br> (depending on the browser).
  We don't want to add a newline or additional elements here, so need to prevent the default behaviour.
  */
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  }

  /*
  The default paste behaviour in a content editable also needs to be overriden.
  This ensures that only plain text is pasted, and prevents any formatting or other non-text elements from being pasted in.
  */
  function handlePaste(e: ClipboardEvent) {
    e.preventDefault();
    // read the clipboard contents as plain text
    const text = e.clipboardData?.getData("text/plain") || "";

    // create a new Text node with the pasted text
    const textNode = document.createTextNode(text);

    // get the current selection
    const selection = window.getSelection();

    if (selection?.rangeCount) {
      // get the first range of the selection
      const range = selection.getRangeAt(0);

      // delete any selected text
      range.deleteContents();

      // insert the new text at the cursor position
      range.insertNode(textNode);

      // move the cursor after the inserted text
      range.setStartAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
</script>

<p
  bind:this={ref}
  on:keydown={handleKeydown}
  on:paste={handlePaste}
  contenteditable="true"
  class="mx-1 my-2 rounded border border-transparent bg-transparent px-1.5 py-1 text-left text-sm font-medium !whitespace-nowrap text-neutral-500 caret-neutral-600 transition-colors duration-500 placeholder:text-neutral-400/60 focus-within:border-community-primary focus:ring-0 focus:outline-none dark:text-white/95 dark:caret-white dark:placeholder:text-neutral-500"
>
  {value}
</p>

<style>
  /* to maintain a minimum area when empty */
  [contenteditable="true"]:empty:before {
    height: 20px;
    min-width: 1ch;
    pointer-events: none;
    display: block; /* for Firefox */
  }
</style>
